import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import {
  type UserPreferences,
  userPreferencesService,
} from '@/services/data/UserPreferencesService';

const PREFERENCES_QUERY_KEY = ['user-preferences'] as const;

/**
 * Hook for managing user preferences
 * Provides methods for getting and updating user settings and acknowledgments
 */
export function useUserPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Query key factory
  const queryKeys = {
    preferences: PREFERENCES_QUERY_KEY,
  };

  // Get user preferences
  const {
    data: preferences,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.preferences,
    queryFn: async () => {
      const response = await userPreferencesService.getUserPreferences();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });

  // Upsert preferences mutation
  const upsertPreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      const response = await userPreferencesService.upsertPreferences(updates);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch preferences
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences });
    },
  });

  // Update specific preference mutation
  const updatePreferenceMutation = useMutation({
    mutationFn: async ({ field, value }: { field: keyof UserPreferences; value: any }) => {
      const response = await userPreferencesService.updatePreference(field, value);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch preferences
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences });
    },
  });

  // Acknowledge competitor privacy mutation
  const acknowledgeCompetitorPrivacyMutation = useMutation({
    mutationFn: async () => {
      const response = await userPreferencesService.acknowledgeCompetitorPrivacy();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch preferences
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences });
    },
  });

  // Computed values
  const hasAcknowledgedCompetitorPrivacy = preferences?.acknowledged_competitor_privacy === true;

  const faceInsightsEnabled: boolean = (() => {
    const prefs = preferences?.analytics_preferences as Record<string, unknown> | null | undefined;
    if (prefs == null || prefs['face_insights_enabled'] === undefined) return true;
    return Boolean(prefs['face_insights_enabled']);
  })();

  const setFaceInsightsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!user?.id) return;
      const currentPrefs = (preferences?.analytics_preferences as Record<string, unknown>) ?? {};
      const updated = { ...currentPrefs, face_insights_enabled: enabled };

      const { error: saveError } = await supabase
        .from('user_preferences')
        .upsert({ user_id: user.id, analytics_preferences: updated }, { onConflict: 'user_id' })
        .eq('user_id', user.id);
      if (saveError) throw new Error(saveError.message);

      if (!enabled) {
        const { error: purgeError } = await supabase.rpc('purge_face_insights', {
          p_user_id: user.id,
        });
        if (purgeError) throw new Error(purgeError.message);
      }

      await queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
    },
    [user?.id, preferences, queryClient],
  );

  return {
    // Data
    preferences,
    hasAcknowledgedCompetitorPrivacy,
    faceInsightsEnabled,

    // Loading states
    isLoading,
    isUpdating: upsertPreferencesMutation.isPending || updatePreferenceMutation.isPending,
    isAcknowledging: acknowledgeCompetitorPrivacyMutation.isPending,

    // Error states
    error,
    updateError: upsertPreferencesMutation.error || updatePreferenceMutation.error,
    acknowledgeError: acknowledgeCompetitorPrivacyMutation.error,

    // Actions
    setFaceInsightsEnabled,
    upsertPreferences: upsertPreferencesMutation.mutate,
    updatePreference: (field: keyof UserPreferences, value: any) =>
      updatePreferenceMutation.mutate({ field, value }),
    acknowledgeCompetitorPrivacy: acknowledgeCompetitorPrivacyMutation.mutate,
    refetch,

    // Async actions for cases where you need the promise
    upsertPreferencesAsync: upsertPreferencesMutation.mutateAsync,
    updatePreferenceAsync: (field: keyof UserPreferences, value: any) =>
      updatePreferenceMutation.mutateAsync({ field, value }),
    acknowledgeCompetitorPrivacyAsync: acknowledgeCompetitorPrivacyMutation.mutateAsync,
  };
}
